import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnInit,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    IMaterialPickupResult,
    MATERIAL_STATUS_CODE,
    MaterialPickupModel,
    MaterialService,
    ModelOptionsLoaderService,
    RETURN_CODE,
    SynchronizationService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import ConfirmationAddMaterialModel from './confirmation-add-material.model';

@Component({
    selector: 'pace-confirmation-add-material',
    templateUrl: './confirmation-add-material.component.html',
    styleUrls: ['./confirmation-add-material.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class ConfirmationAddMaterialComponent implements OnInit {
    @Input()
    public model: ConfirmationAddMaterialModel;

    public displayAWBNumber: boolean;
    public displayBatchNumber: boolean;
    public pickupRunning = false;

    public confirmationAddMaterialFormChildren: FormArray;
    public confirmationAddMaterialForm: FormGroup;

    public modalResult = new EventEmitter<IMaterialPickupResult>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly appNotificationService: AppNotificationService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly modelOptionsLoader: ModelOptionsLoaderService,
        private readonly materialService: MaterialService,
        private readonly synchronizationService: SynchronizationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.displayBatchNumber = this.configService.getDisplayBatchNumber();
        this.displayAWBNumber = this.configService.getDisplayAddMaterialButton() && !this.displayBatchNumber;
    }

    public ngOnInit(): void {
        this.confirmationAddMaterialFormChildren = this.formBuilder.array([]);
        this.confirmationAddMaterialForm = this.formBuilder.group({
            formChildren: this.confirmationAddMaterialFormChildren,
        });

        this.onTypeChange();
        this.confirmationAddMaterialForm.statusChanges.subscribe(() => this.changeDetectorRef.detectChanges());
    }

    public onTypeChange(): void {
        if (this.model.materialType.selectedEntry) {
            // If a user select the "Siemens Part" then it should be Central Return
            // If a user select the "Non-Siemens Part / Dealer Part" then it should be Local Scrap
            const returnOption =
                this.model.materialType.selectedEntry.value === ConfigConstants.siemensPartMaterialTypeCode
                    ? RETURN_CODE.CENTRAL_RETURN
                    : RETURN_CODE.LOCAL_SCRAP;
            this.model.newMaterial.localScrapReturnConsumed = returnOption;
            this.model.newMaterial.localScrapReturnWithoutConsumption = returnOption;

            this.setModelOptionsForMaterialNumber();
        }
    }

    private setModelOptionsForMaterialNumber(): void {
        // If user selects the "Non-Siemens Part / Dealer Part" then Material Number can also be text
        if (this.model.materialType.selectedEntry.value === ConfigConstants.nonSiemensPartMaterialTypeCode) {
            this.modelOptionsLoader.load(
                this.model.newMaterial.materialIdentifier.materialNumber,
                'MaterialConsumptionAdd',
                'MaterialNumberText',
            );
        } else {
            this.modelOptionsLoader.load(
                this.model.newMaterial.materialIdentifier.materialNumber,
                'MaterialConsumptionAdd',
                'MaterialNumber',
            );
        }
        if (this.model.newMaterial.materialIdentifier.materialNumber.value.length > 0) {
            this.confirmationAddMaterialFormChildren.controls.forEach((control) => control.updateValueAndValidity());
        }
    }

    private setModelResultAndClose(): void {
        this.modalResult.next({
            materialModel: this.model.newMaterial,
            statusCode: MATERIAL_STATUS_CODE.PICKED,
            pickedQuantity: this.model.newMaterial.quantity.value,
        });
        this.modalResult.complete();
        this.modalInstance.hide();
    }

    public pickup(): void {
        this.pickupRunning = true;
        if (this.displayBatchNumber) {
            // use same batch number to declare Material return
            this.model.newMaterial.batchNumber.value = this.model.newMaterial.awbReturn.value;
        }
        const newMaterialPickupModel = new MaterialPickupModel(
            this.model.newMaterial.notificationIdentifier,
            this.model.newMaterial,
        );

        this.materialService
            .pickupMaterial(newMaterialPickupModel)
            .pipe(
                take(1),
                tap(() => {
                    this.setModelResultAndClose();
                }),
                mergeMap(() => {
                    this.log.debug('ConfirmationAddMaterialModel: pickup: sendOfflineData');
                    return this.synchronizationService.sendOfflineData();
                }),
                catchError((error) => {
                    this.log.error(`ConfirmationAddMaterialModel: pickup: sendOfflineData: ERROR:`, error);
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_PICKUPMISSING_REQUEST_ERROR_NO_TITLE'),
                        <string>this.translate.instant('CONFIRMATION_MATERIAL_PICKUPMISSING_REQUEST_ERROR_NO_BODY'),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                this.pickupRunning = false;
            });
    }
}
